"use client";

import React, { useEffect, useState, useRef } from "react";
import {
  HomePageMinimalSectionShell,
  HomePageSectionData,
  fetchHomePageSectionById,
} from "@/api-service/homePage";
import { ComponentMap } from "../common/ComponentMap";

interface LazySectionShellProps {
  shell: HomePageMinimalSectionShell;
  isFirst?: boolean;
}

function SectionSkeleton({ type }: { type: string; title: string }) {
  const typeKey = (type || "").toUpperCase();

  if (typeKey === "SLIDERS") {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 py-4 animate-pulse">
        <div className="w-full h-[250px] sm:h-[400px] md:h-[480px] bg-slate-200 rounded-2xl" />
      </div>
    );
  }

  if (typeKey === "BRANDS") {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 py-8 space-y-4 animate-pulse">
        <div className="h-6 w-48 bg-slate-200 rounded-md" />
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (typeKey === "CATEGORIES") {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 py-8 space-y-4 animate-pulse">
        <div className="h-6 w-48 bg-slate-200 rounded-md" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-36 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // CATEGORY_PRODUCTS or default
  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 py-8 space-y-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-6 w-56 bg-slate-200 rounded-md" />
        <div className="h-4 w-20 bg-slate-200 rounded-md" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-64 bg-slate-200 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export default function LazySectionShell({ shell, isFirst = false }: LazySectionShellProps) {
  const [sectionData, setSectionData] = useState<HomePageSectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(isFirst);
  const containerRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver for lazy loading as user scrolls
  useEffect(() => {
    if (isFirst || shouldFetch) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldFetch(true);
          observer.disconnect();
        }
      },
      { rootMargin: "250px 0px" } // Fetch 250px before entering viewport
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isFirst, shouldFetch]);

  // Fetch section data on demand
  useEffect(() => {
    if (!shouldFetch) return;

    let isMounted = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const res = await fetchHomePageSectionById(shell.id);
        console.log(`Fetched section ${shell.id}:`, res);
        if (isMounted && res?.data) {
          setSectionData(res.data);
        }
      } catch (err) {
        console.error(`Failed to lazy load section ${shell.id}:`, err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [shell.id, shouldFetch]);

  const typeKey = (shell.sectionType || "").toUpperCase();
  const Component = ComponentMap[typeKey];

  if (!Component) return null;

  return (
    <div ref={containerRef} className="w-full">
      {loading || !sectionData ? (
        <SectionSkeleton type={shell.sectionType} title={shell.title} />
      ) : (
        <Component section={sectionData} />
      )}
    </div>
  );

}
